import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
} from "chart.js";
import { Icons } from "./Machine360Icons";
import Nodata from "../common-component/Nodata";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip
);

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 Days" },
  { value: 30, label: "Last 30 Days" },
  { value: 90, label: "Last 90 Days" },
];

export const CostTrendChart = ({
  data,
  isLoading = false,
  selectedDays = 30,
  onPeriodChange,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(selectedDays);

  useEffect(() => {
    setSelectedPeriod(selectedDays);
  }, [selectedDays]);

  const handlePeriodChange = (e) => {
    const val = Number(e.target.value);
    setSelectedPeriod(val);
    if (onPeriodChange) {
      onPeriodChange(val);
    }
  };

  if (isLoading) {
    return (
      <div className="m360-card m360-cost-trend-card">
        <div className="m360-card-header">
          <div className="m360-card-title-group">
            <Skeleton circle={true} height={16} width={16} />
            <Skeleton height={16} width={150} />
          </div>
          <div className="m360-card-actions">
            <Skeleton height={28} width={110} borderRadius={4} />
          </div>
        </div>

        <div
          className="m360-cost-chart-wrap d-flex flex-column justify-content-end px-3"
          style={{ height: 210, paddingBottom: 16 }}
        >
          <Skeleton height={160} width="100%" borderRadius={4} />
        </div>
      </div>
    );
  }

  const title = data?.title || "Earning Trend";
  const hasCostData = Boolean(
    data?.data &&
    Array.isArray(data.data) &&
    data.data.some((v) => Number(v) > 0)
  );

  const chartData = {
    labels: data?.xAxisTickLabels || data?.labels || [],
    datasets: [
      {
        label: "Earning (USD)",
        data: data?.data || [],
        borderColor: "#2490d2",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, "rgba(36, 144, 210, 0.18)");
          gradient.addColorStop(1, "rgba(36, 144, 210, 0.0)");
          return gradient;
        },
        fill: true,
        tension: 0.35,
        pointBackgroundColor: "#2490d2",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 3.5,
        pointHoverRadius: 5.5,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(17, 17, 17, 0.92)",
        padding: 8,
        cornerRadius: 4,
        titleFont: { family: "'Sora', sans-serif", size: 11 },
        bodyFont: { family: "'Sora', sans-serif", size: 11 },
        callbacks: {
          label: (context) => ` Earning: $${Number(context.raw).toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: false,
        },
      },
      y: {
        min: 0,
        suggestedMax: 0.2,
        ticks: {
          stepSize: 0.05,
          color: "#94a3b8",
          font: { size: 10 },
          callback: (value) => `$${Number(value).toFixed(2)}`,
        },
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
        title: {
          display: true,
          text: data?.yAxisLabel || "Earning (USD)",
          color: "#94a3b8",
          font: { size: 11 },
        },
      },
    },
  };

  return (
    <div className="m360-card m360-cost-trend-card">
      <div className="m360-card-header">
        <div className="m360-card-title-group">
          <Icons.Dollar size={16} className="m360-card-header-icon" />
          <h4 className="m360-card-title">{title}</h4>
        </div>
        <div className="m360-card-actions">
          <select
            className="m360-select-dropdown"
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!hasCostData ? (
        <div className="m360-card-nodata-wrapper">
          <Nodata />
        </div>
      ) : (
        <div className="m360-cost-chart-wrap">
          <Line data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};

export const EarningTrendChart = CostTrendChart;

